/**
 * audioStorage.js — IndexedDB-based audio blob persistence
 * Survives page refreshes, deploys, and service worker updates.
 * Stores the last recorded audio blob so it can be transcribed after recovery.
 */

const DB_NAME = 'writeright_audio';
const STORE_NAME = 'recordings';
const KEY = 'last_recording';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Save an audio blob + metadata to IndexedDB */
export async function saveAudioBlob(blob, metadata = {}) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put({ blob, savedAt: new Date().toISOString(), ...metadata }, KEY);
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => { db.close(); resolve(true); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch (e) {
    console.warn('Failed to save audio to IndexedDB:', e);
    return false;
  }
}

/** Load saved audio blob from IndexedDB */
export async function loadAudioBlob() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(KEY);
    return new Promise((resolve, reject) => {
      request.onsuccess = () => { db.close(); resolve(request.result || null); };
      request.onerror = () => { db.close(); reject(request.error); };
    });
  } catch (e) {
    console.warn('Failed to load audio from IndexedDB:', e);
    return null;
  }
}

/** Clear saved audio blob from IndexedDB */
export async function clearAudioBlob() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.delete(KEY);
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => { db.close(); resolve(true); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch (e) {
    console.warn('Failed to clear audio from IndexedDB:', e);
    return false;
  }
}
